import type { StudioController } from '../studio/StudioController';
import type { DirectionSettings, StudioAsset, StudioScene, StudioShot, StudioState } from '../studio/types';

type Cleanup = () => void;

const CAMERA_PRESETS: DirectionSettings['cameraPreset'][] = ['pov', 'portrait', 'follow', 'wide', 'low', 'crane', 'orbit'];
const TIMES: DirectionSettings['timeOfDay'][] = ['morning', 'golden', 'forest', 'blue-hour'];
const ACTIONS: DirectionSettings['action'][] = ['idle', 'look-out', 'run', 'investigate', 'repair', 'comfort', 'group-promise'];
const EXPRESSIONS: DirectionSettings['expression'][] = ['curious', 'hopeful', 'joy', 'alert', 'focused', 'kind', 'wonder'];

export class StudioUI {
  private readonly cleanup: Cleanup[] = [];
  private readonly root: HTMLElement;
  private lastSceneId = '';

  constructor(private readonly controller: StudioController, root: HTMLElement | Document = document) {
    const app = root.querySelector<HTMLElement>('#studio-app');
    if (!app) throw new Error('Missing #studio-app element.');
    this.root = app;
    this.bindEvents();
    this.render(this.controller.state);
  }

  dispose(): void {
    for (const remove of this.cleanup.splice(0)) remove();
  }

  private bindEvents(): void {
    this.listen(this.root, 'click', (event) => {
      const target = (event.target as Element).closest<HTMLElement>('[data-action]');
      if (!target) return;
      const { action, id, panel } = target.dataset;
      if (action === 'select-scene' && id) this.controller.selectScene(id);
      if (action === 'select-shot' && id) this.controller.selectShot(id);
      if (action === 'toggle-playback') this.controller.togglePlayback();
      if (action === 'jump-start') this.controller.seek(0);
      if (action === 'jump-end') this.controller.seek(this.controller.duration);
      if (action === 'capture') this.controller.requestCapture();
      if (action === 'export') this.controller.requestExport();
      if (action === 'select-asset' && id) this.controller.selectAsset(id);
      if (action === 'toggle-panel') this.controller.setMobilePanel((panel as StudioState['mobilePanel']) ?? null);
      if (action === 'close-panel') this.controller.setMobilePanel(this.controller.state.mobilePanel);
    });

    this.listen(this.root, 'input', (event) => {
      const input = event.target as HTMLInputElement;
      if (input.matches('[data-control="playhead"]')) this.controller.seek(Number(input.value));
      if (input.matches('[data-direction="lensMm"]')) this.controller.updateDirection({ lensMm: Number(input.value) });
    });

    this.listen(this.root, 'change', (event) => {
      const input = event.target as HTMLInputElement | HTMLSelectElement;
      const key = input.dataset.direction as keyof DirectionSettings | undefined;
      if (key && key !== 'lensMm') this.controller.updateDirection({ [key]: input.value } as Partial<DirectionSettings>);
      if (input.dataset.capture === 'format') this.controller.updateCapture({ format: input.value as StudioState['capture']['format'] });
      if (input.dataset.capture === 'resolution') this.controller.updateCapture({ resolution: input.value as StudioState['capture']['resolution'] });
      if (input.dataset.capture === 'includeGuides') this.controller.updateCapture({ includeGuides: (input as HTMLInputElement).checked });
    });

    this.listen(window, 'keydown', (event) => {
      const keyboardEvent = event as KeyboardEvent;
      if (keyboardEvent.code !== 'Space' || (keyboardEvent.target as HTMLElement)?.matches('input, select, button, textarea')) return;
      keyboardEvent.preventDefault();
      this.controller.togglePlayback();
    });

    this.cleanup.push(this.controller.on('studio:statechange', (event) => this.render(event.detail)));
  }

  private render(state: Readonly<StudioState>): void {
    this.root.dataset.mobilePanel = state.mobilePanel ?? '';
    this.bindText('episode-title', state.episode.title);
    this.bindText('scene-title', this.controller.scene.title);
    this.bindText('shot-title', this.controller.shot.title);
    this.bindText('shot-number', `SHOT ${this.controller.shot.number}`);
    this.bindText('shot-note', this.controller.shot.note);

    const playhead = this.root.querySelector<HTMLInputElement>('[data-control="playhead"]')!;
    playhead.max = String(this.controller.duration);
    playhead.value = String(state.currentTime);
    playhead.style.setProperty('--progress', `${this.controller.duration ? (state.currentTime / this.controller.duration) * 100 : 0}%`);
    playhead.setAttribute('aria-valuetext', this.formatTime(state.currentTime));
    this.bindText('current-time', this.formatTime(state.currentTime));
    this.bindText('duration', this.formatTime(this.controller.duration));

    const play = this.root.querySelector<HTMLButtonElement>('[data-action="toggle-playback"]')!;
    play.dataset.playing = String(state.isPlaying);
    play.setAttribute('aria-label', state.isPlaying ? 'Pause scene' : 'Play scene');
    play.title = state.isPlaying ? 'Pause (Space)' : 'Play (Space)';

    const sceneList = this.root.querySelector<HTMLElement>('[data-list="scenes"]')!;
    if (sceneList.dataset.rendered !== state.episode.id) {
      sceneList.innerHTML = state.episode.scenes.map((scene) => this.sceneMarkup(scene, state.selectedSceneId)).join('');
      sceneList.dataset.rendered = state.episode.id;
    }
    sceneList.querySelectorAll<HTMLElement>('[data-action="select-scene"]').forEach((element) => {
      const selected = element.dataset.id === state.selectedSceneId;
      element.classList.toggle('is-selected', selected);
      element.setAttribute('aria-current', selected ? 'true' : 'false');
    });

    const timeline = this.root.querySelector<HTMLElement>('[data-list="shots"]')!;
    if (this.lastSceneId !== state.selectedSceneId) {
      timeline.innerHTML = this.controller.scene.shots.map((shot) => this.shotMarkup(shot, state.selectedShotId)).join('');
      this.lastSceneId = state.selectedSceneId;
    }
    timeline.querySelectorAll<HTMLElement>('[data-action="select-shot"]').forEach((element) => {
      const selected = element.dataset.id === state.selectedShotId;
      element.classList.toggle('is-selected', selected);
      element.setAttribute('aria-current', selected ? 'true' : 'false');
    });

    const assets = this.root.querySelector<HTMLElement>('[data-list="assets"]')!;
    if (!assets.dataset.rendered) {
      assets.innerHTML = this.controller.assets.map((asset) => this.assetMarkup(asset)).join('');
      assets.dataset.rendered = 'true';
    }
    assets.querySelectorAll<HTMLElement>('[data-action="select-asset"]').forEach((element) => {
      const selected = element.dataset.id === state.selectedAssetId;
      element.classList.toggle('is-selected', selected);
      element.setAttribute('aria-pressed', String(selected));
    });

    this.setSelect('cameraPreset', state.direction.cameraPreset, CAMERA_PRESETS);
    this.setSelect('timeOfDay', state.direction.timeOfDay, TIMES);
    this.setSelect('action', state.direction.action, ACTIONS);
    this.setSelect('expression', state.direction.expression, EXPRESSIONS);
    const lens = this.root.querySelector<HTMLInputElement>('[data-direction="lensMm"]')!;
    lens.value = String(state.direction.lensMm);
    this.bindText('lens-value', `${state.direction.lensMm} mm`);

    this.setCapture('format', state.capture.format);
    this.setCapture('resolution', state.capture.resolution);
    this.root.querySelector<HTMLInputElement>('[data-capture="includeGuides"]')!.checked = state.capture.includeGuides;
  }

  private setSelect<K extends Exclude<keyof DirectionSettings, 'lensMm'>>(key: K, value: DirectionSettings[K], options: DirectionSettings[K][]): void {
    const select = this.root.querySelector<HTMLSelectElement>(`[data-direction="${key}"]`)!;
    if (!select.dataset.rendered) {
      select.innerHTML = options.map((option) => `<option value="${option}">${this.humanize(option)}</option>`).join('');
      select.dataset.rendered = 'true';
    }
    select.value = value;
  }

  private setCapture(key: 'format' | 'resolution', value: string): void {
    this.root.querySelector<HTMLSelectElement>(`[data-capture="${key}"]`)!.value = value;
  }

  private sceneMarkup(scene: StudioScene, selectedId: string): string {
    return `<button class="scene-slip ${scene.id === selectedId ? 'is-selected' : ''}" type="button" data-action="select-scene" data-id="${scene.id}">
      <span class="scene-number">${scene.number}</span><span class="scene-copy"><strong>${scene.title}</strong><small>${scene.summary}</small></span><span class="scene-count">${scene.shots.length}</span>
    </button>`;
  }

  private shotMarkup(shot: StudioShot, selectedId: string): string {
    const width = Math.max(108, shot.duration * 32);
    return `<button class="shot-clip ${shot.id === selectedId ? 'is-selected' : ''}" type="button" data-action="select-shot" data-id="${shot.id}" style="--clip-color:${shot.color};--clip-width:${width}px">
      <span class="clip-frame" aria-hidden="true"><i></i><i></i><i></i></span><span class="clip-copy"><b>${shot.number}</b><span>${shot.title}</span><small>${shot.duration.toFixed(1)}s</small></span>
    </button>`;
  }

  private assetMarkup(asset: StudioAsset): string {
    return `<button class="asset-tile" type="button" data-action="select-asset" data-id="${asset.id}" style="--asset-color:${asset.swatch}" aria-pressed="false">
      <span class="asset-portrait"><i>${asset.glyph}</i></span><span class="asset-name">${asset.name}</span><span class="asset-kind">${asset.kind}${asset.status === 'Draft' ? ' · draft' : ''}</span>
    </button>`;
  }

  private bindText(key: string, value: string): void {
    this.root.querySelector<HTMLElement>(`[data-bind="${key}"]`)!.textContent = value;
  }

  private humanize(value: string): string {
    return value.replaceAll('-', ' ').replace(/^./, (letter) => letter.toUpperCase());
  }

  private formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60).toString().padStart(2, '0');
    const remainder = Math.floor(seconds % 60).toString().padStart(2, '0');
    const frames = Math.floor((seconds % 1) * 24).toString().padStart(2, '0');
    return `${minutes}:${remainder}:${frames}`;
  }

  private listen(target: EventTarget, type: string, listener: EventListener): void {
    target.addEventListener(type, listener);
    this.cleanup.push(() => target.removeEventListener(type, listener));
  }
}
